<template>
  <div
    :class="[
      'site-filter-toolbar h-full w-full flex align-items-center gap-3',
      embedded ? 'border-bottom-none' : 'surface-0 px-4 p-3 border-bottom-1',
    ]"
  >
    <InputGroup class="w-full">
      <InputText
        v-for="filter in filter_columns.filter((filter) => filter.input_type === 'search')"
        :key="filter.field"
        v-model="filters[filter.field]"
        type="search"
        :aria-label="filter.label || 'Search results'"
        @update:modelValue="(value) => updateFilter(value, filter.field, filter.input_type)"
        placeholder="Search..."
        class="site-filter-search-input h-3rem"
      />
      <Button
        label="Filter / Sort"
        class="site-filter-toggle white-space-nowrap h-3rem"
        aria-haspopup="dialog"
        :aria-expanded="showFilters"
        @click="showFilters = !showFilters"
      />
    </InputGroup>
    <Dialog
      v-model:visible="showFilters"
      header="Filter and sort results"
      modal
      blockScroll
      :draggable="false"
      :breakpoints="filterDialogBreakpoints"
      :style="{ width: '32rem' }"
      :pt="filterDialogPassThrough"
      @hide="showFilters = false"
    >
      <div class="site-filter-dialog-body flex flex-column gap-3 p-3 md:p-4 w-full">
        <p class="site-filter-dialog-intro m-0">
          Narrow the results and choose how they are ordered.
        </p>

        <section
          v-if="dialogFilterColumns.length"
          class="site-filter-section site-filter-section--controls surface-0 border-1 relative z-1 overflow-visible"
        >
          <div class="site-filter-section-header flex align-items-start gap-3 p-3 border-bottom-1">
            <span
              class="site-filter-section-icon flex align-items-center justify-content-center flex-shrink-0 border-circle"
            >
              <i class="pi pi-filter" aria-hidden="true"></i>
            </span>
            <div>
              <h2 class="site-filter-section-heading m-0 text-base font-bold">Filters</h2>
              <p class="site-filter-section-copy mt-1 mb-0">Show only the results you need.</p>
            </div>
          </div>

          <div class="flex flex-column gap-3 p-3">
            <div v-for="filter in dialogFilterColumns" :key="filter.field">
              <template v-if="filter.input_type === 'dropdown'">
                <label
                  :for="`site-filter-${filter.field}`"
                  class="site-filter-label block mb-2 font-semibold"
                >
                  {{ filter.label }}
                </label>
                <Dropdown
                  :inputId="`site-filter-${filter.field}`"
                  v-model="filters[filter.field]"
                  :options="allFilters?.[filter.field] || []"
                  :optionLabel="filter.display ?? 'name'"
                  optionValue="id"
                  :placeholder="filter.label"
                  filter
                  showClear
                  :loading="filterOptionsPending"
                  class="site-filter-control w-full"
                  :pt="{
                    clearIcon: 'z-5',
                    filterInput: 'w-full',
                    panel: 'w-full',
                    list: 'max-h-10rem',
                    itemLabel: 'w-full text-xxs white-space-normal',
                    input: 'text-sm',
                  }"
                  append-to="self"
                  @update:modelValue="
                    (value) => updateFilter(value, filter.field, filter.input_type)
                  "
                />
              </template>

              <div v-else-if="filter.input_type === 'date'" class="w-full">
                <template v-if="path === 'resources/courses'">
                  <label
                    :for="`site-filter-${filter.field}`"
                    class="site-filter-label block mb-2 font-semibold"
                  >
                    {{ filter.label }}
                  </label>
                  <Dropdown
                    :inputId="`site-filter-${filter.field}`"
                    v-model="filters[filter.field]"
                    :options="
                      enrolmentStartDates.map((date) => ({
                        name: date.toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                        value: date,
                      }))
                    "
                    optionLabel="name"
                    optionValue="value"
                    placeholder="Enrolment start date"
                    showClear
                    class="site-filter-control w-full"
                    :pt="{
                      clearIcon: 'z-5',
                      filterInput: 'w-full',
                      panel: 'w-full',
                      list: 'max-h-10rem',
                      itemLabel: 'w-full text-xxs white-space-normal',
                      input: 'text-sm',
                      transition: { css: false },
                    }"
                    append-to="self"
                    @update:modelValue="updateCourseEnrolmentFilter"
                  />
                </template>

                <template v-else-if="isEventLanding">
                  <label
                    :for="`site-filter-${filter.field}`"
                    class="site-filter-label block mb-2 font-semibold"
                  >
                    {{ filter.label }}
                  </label>
                  <Dropdown
                    :inputId="`site-filter-${filter.field}`"
                    v-model="filters[filter.field]"
                    :options="eventTypes"
                    optionLabel="name"
                    optionValue="value"
                    placeholder="Event Start Date"
                    showClear
                    class="site-filter-control w-full"
                    :pt="{
                      clearIcon: 'z-5',
                      filterInput: 'w-full',
                      panel: 'w-full',
                      list: 'max-h-10rem',
                      itemLabel: 'w-full text-xxs white-space-normal',
                      input: 'text-sm',
                      transition: { css: false },
                    }"
                    append-to="self"
                    @update:modelValue="
                      (value) => updateEventType(value, filter.field, filter.input_type)
                    "
                  />
                </template>

                <template v-else>
                  <div class="site-filter-date-grid">
                    <div>
                      <label
                        :for="`site-filter-${filter.field}-condition`"
                        class="site-filter-label block mb-2 font-semibold"
                      >
                        Date condition
                      </label>
                      <Dropdown
                        :inputId="`site-filter-${filter.field}-condition`"
                        v-model="dateMatchMode"
                        :options="dateMatchModes"
                        optionLabel="name"
                        optionValue="id"
                        class="site-filter-control w-full"
                        :pt="{
                          clearIcon: 'z-5',
                          filterInput: 'w-full',
                          panel: 'w-full',
                          list: 'max-h-10rem',
                          itemLabel: 'w-full text-xxs white-space-normal',
                          input: 'text-sm',
                        }"
                        append-to="self"
                        @update:modelValue="(value) => updateDateMatchMode(value, filter.field)"
                      />
                    </div>
                    <div>
                      <label
                        :for="`site-filter-${filter.field}`"
                        class="site-filter-label block mb-2 font-semibold"
                      >
                        {{ filter.label }}
                      </label>
                      <Calendar
                        :inputId="`site-filter-${filter.field}`"
                        class="site-filter-control w-full"
                        touchUI
                        showButtonBar
                        hideOnRangeSelection
                        :view="filter.view ?? 'date'"
                        v-model="filters[filter.field]"
                        :selectionMode="dateMatchMode == RANGE ? 'range' : 'single'"
                        :manualInput="false"
                        :dateFormat="filter.view === 'month' ? 'yy-mm' : 'yy-mm-dd'"
                        :placeholder="filter.label"
                        @update:modelValue="
                          (value) => updateFilter(value, filter.field, filter.input_type)
                        "
                        :pt="{
                          input: 'text-sm',
                        }"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="sort_columns.length > 0"
          class="site-filter-section overflow-hidden surface-0 border-1"
        >
          <div class="site-filter-section-header flex align-items-start gap-3 p-3 border-bottom-1">
            <span
              class="site-filter-section-icon flex align-items-center justify-content-center flex-shrink-0 border-circle"
            >
              <i class="pi pi-sort-alt" aria-hidden="true"></i>
            </span>
            <div>
              <h2 class="site-filter-section-heading m-0 text-base font-bold">Sorting</h2>
              <p class="site-filter-section-copy mt-1 mb-0">
                Drag to set priority, then choose a direction.
              </p>
            </div>
          </div>

          <div class="p-3">
            <DataTable
              :value="orderedSortColumns"
              class="site-filter-sort-table text-sm"
              size="small"
              :pt="sortTablePassThrough"
              :tableProps="{ 'aria-label': 'Sort fields' }"
              @rowReorder="onRowReorder"
            >
              <Column
                rowReorder
                header="Order"
                headerStyle="width: 4.5rem"
                :reorderableColumn="false"
              />

              <Column header="Direction">
                <template #body="{ data }">
                  <div class="site-filter-sort-direction flex align-items-center gap-2">
                    <TriStateCheckbox
                      :inputId="`site-sort-${data.field}`"
                      v-model="sortOptions[data.field]"
                      :aria-label="`${data.label}: ${getSortDirectionLabel(data.field)}`"
                      @update:modelValue="() => updateSort()"
                    >
                      <template #checkicon>
                        <i class="pi pi-sort-amount-up" aria-hidden="true"></i>
                      </template>
                      <template #uncheckicon>
                        <i class="pi pi-sort-amount-down" aria-hidden="true"></i>
                      </template>
                    </TriStateCheckbox>
                    <label
                      :for="`site-sort-${data.field}`"
                      class="site-filter-sort-label cursor-pointer"
                    >
                      {{ getSortDirectionLabel(data.field) }}
                    </label>
                  </div>
                </template>
              </Column>

              <Column header="Field">
                <template #body="{ data }">
                  <span class="site-filter-sort-field font-medium">{{ data.label }}</span>
                </template>
              </Column>
            </DataTable>
          </div>
        </section>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
const route = useRoute()
const config = useConfigStore()

const ON = ref(0)
const RANGE = ref(1)
const BEFORE = ref(2)
const AFTER = ref(3)

const props = defineProps({
  sort_columns: Array,
  filter_columns: Array,
  embedded: {
    type: Boolean,
    default: false,
  },
})

const filters = ref({})
const sortOptions = ref({})
const orderedSortColumns = ref([...props.sort_columns])
const dialogFilterColumns = computed(() =>
  props.filter_columns.filter((filter) => ['date', 'dropdown'].includes(filter.input_type))
)

const filterDialogBreakpoints = {
  '640px': 'calc(100vw - 2rem)',
}

const filterDialogPassThrough = {
  mask: { class: 'p-3' },
  root: {
    class: 'site-filter-dialog relative overflow-hidden bg-white border-1 border-round-2xl',
  },
  header: {
    class: 'site-filter-dialog-header border-none px-3 md:px-4 pt-4 pb-3',
  },
  title: { class: 'site-filter-dialog-title font-bold' },
  closeButton: { class: 'site-filter-close-button' },
  content: { class: 'site-filter-dialog-content p-0' },
}

const sortTablePassThrough = {
  wrapper: {
    class: 'overflow-hidden border-1',
    style: {
      borderColor: 'var(--sats-border)',
      borderRadius: '0.7rem',
    },
  },
  table: { class: 'w-full' },
}

const filterPath = route.path.slice(1)

const { data: allFilters, pending: filterOptionsPending } = await useLazyAsyncData(
  `all-filter-options-${filterPath}`,
  () => $fetch('/api/filter-options', { query: { path: filterPath } })
)

const showFilters = ref(false)
const path = ref(route.path.slice(1))
const isEventLanding = ['resources/events', 'resources/live-events'].includes(path.value)
const dateMatchModes = ref([
  {
    id: ON.value,
    name: 'On',
  },
  {
    id: RANGE.value,
    name: 'Range',
  },
  {
    id: BEFORE.value,
    name: 'Before',
  },
  {
    id: AFTER.value,
    name: 'After',
  },
])

const eventTypes = ref([
  {
    value: AFTER.value,
    name: 'Upcoming',
  },
  {
    value: BEFORE.value,
    name: 'Past',
  },
])

function updateEventType(value, field, input_type) {
  dateMatchMode.value = value
  config.queries[path.value].skip = 0

  if (value === null || value === undefined) {
    delete config.queries[path.value].where[field]
    return
  }

  updateFilter(new Date(), field, input_type)
}

const enrolmentStartDates = computed(() => {
  const now = new Date()
  const currentYear = now.getFullYear()

  return ['1 February', '1 May', '1 August', '1 November']
    .map((md) => {
      const date = new Date(`${md} ${currentYear}`)

      if (date < now) {
        date.setFullYear(currentYear + 1)
      }

      return date
    })
    .sort((a, b) => a.getTime() - b.getTime())
})
const dateMatchMode = ref(isEventLanding ? AFTER.value : ON.value)

const updateSort = () => {
  const sortObj = {}

  for (const col of [...orderedSortColumns.value].reverse()) {
    const dir = sortOptions.value[col.field]
    if (dir !== null && dir !== undefined) {
      sortObj[col.field] = dir ? 1 : -1
    }
  }
  config.queries[path.value].sort = sortObj
}

const getSortDirectionLabel = (field) => {
  const direction = sortOptions.value[field]

  if (direction === true) return 'Ascending'
  if (direction === false) return 'Descending'
  return 'Not sorted'
}

const onRowReorder = (event) => {
  orderedSortColumns.value = event.value
  updateSort()
}

const updateDateMatchMode = (value, field) => {
  dateMatchMode.value = value
  filters.value[field] = null
  config.queries[path.value].skip = 0
  delete config.queries[path.value].where[field]
}

const updateFilter = (value, field, inputType) => {
  config.queries[path.value].skip = 0

  if ([null, undefined].includes(value)) {
    delete config.queries[path.value].where?.[field]
  } else {
    let filter = {}
    switch (inputType) {
      case 'search':
        value = value.toLowerCase().trim().replace(/\s+/g, ' ')
        if (value) {
          filter = {
            $or: [
              { [field]: { $icontains: value } },
              ...(path.value === 'resources/courses' ? [{ code: { $icontains: value } }] : []),
              {
                slug: {
                  $icontains: value.replace(/[^a-z0-9\s]/g, '').replace(/ /g, '-'),
                },
              },
            ],
          }
        } else {
          delete config.queries[path.value].where?.$or
        }
        break
      case 'dropdown':
        filter = {
          [field]: {
            $ne: null,
            $exists: true,
            $or: [{ $eq: value }, { $in: value }],
          },
        }
        break
      case 'date':
        switch (dateMatchMode.value) {
          case ON.value:
            filter = {
              [field]: getDateString(value),
            }
            break
          case RANGE.value:
            if (value.every((element) => element !== null)) {
              filter = {
                [field]: {
                  $gte: getDateString(value[0]),
                  $lte: getDateString(value[1]),
                },
              }
            }
            break
          case BEFORE.value:
            filter = {
              [field]: {
                $lte: getDateString(value),
              },
            }
            break
          case AFTER.value:
            filter = {
              [field]: {
                $gte: getDateString(value),
              },
            }
            break
        }
        break
    }
    Object.assign(config.queries[path.value].where, filter)
  }
}

const updateCourseEnrolmentFilter = (value) => {
  const where = config.queries[path.value].where
  config.queries[path.value].skip = 0

  delete where.enrolment_start_date
  delete where.enrolment_end_date

  if (!value) return

  const selectedDate = getDateString(value)
  Object.assign(where, {
    enrolment_start_date: {
      $lte: selectedDate,
    },
    enrolment_end_date: {
      $gte: selectedDate,
    },
  })
}

const getDateString = (dt) => {
  const date = new Date(dt)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getLocalDate = (value) => {
  if (!value) return null

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null

  const [, year, month, day] = match.map(Number)
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const getSearchValue = (where, field) => {
  if (typeof where[field] === 'string') return where[field]
  if (!Array.isArray(where.$or)) return null

  const condition = where.$or.find((item) => typeof item?.[field]?.$icontains === 'string')
  return condition?.[field]?.$icontains ?? null
}

const getDropdownValue = (condition) => {
  if (condition === null || condition === undefined) return null
  if (typeof condition !== 'object') return condition
  if (!Array.isArray(condition.$or)) return null

  const equalsCondition = condition.$or.find((item) => item && Object.hasOwn(item, '$eq'))
  if (equalsCondition) return equalsCondition.$eq

  const includesCondition = condition.$or.find((item) => item && Object.hasOwn(item, '$in'))
  return includesCondition?.$in ?? null
}

const restoreDateControl = (filter, where) => {
  if (path.value === 'resources/courses') {
    const startDate = where.enrolment_start_date?.$lte
    const endDate = where.enrolment_end_date?.$gte
    if (startDate && startDate === endDate) {
      filters.value[filter.field] = getLocalDate(startDate)
    }
    return
  }

  const condition = where[filter.field]
  if (!condition) return

  if (isEventLanding) {
    if (condition.$lte) {
      dateMatchMode.value = BEFORE.value
      filters.value[filter.field] = BEFORE.value
    } else if (condition.$gte) {
      dateMatchMode.value = AFTER.value
      filters.value[filter.field] = AFTER.value
    }
    return
  }

  if (typeof condition === 'string') {
    dateMatchMode.value = ON.value
    filters.value[filter.field] = getLocalDate(condition)
  } else if (condition.$gte && condition.$lte) {
    dateMatchMode.value = RANGE.value
    filters.value[filter.field] = [getLocalDate(condition.$gte), getLocalDate(condition.$lte)]
  } else if (condition.$lte) {
    dateMatchMode.value = BEFORE.value
    filters.value[filter.field] = getLocalDate(condition.$lte)
  } else if (condition.$gte) {
    dateMatchMode.value = AFTER.value
    filters.value[filter.field] = getLocalDate(condition.$gte)
  }
}

const restoreSortControls = (sort) => {
  const columnsByField = new Map(props.sort_columns.map((column) => [column.field, column]))
  const activeFields = Object.keys(sort).filter((field) => columnsByField.has(field))

  for (const column of props.sort_columns) {
    sortOptions.value[column.field] = null
  }
  for (const field of activeFields) {
    sortOptions.value[field] = sort[field] === 1 ? true : sort[field] === -1 ? false : null
  }

  const activeFieldSet = new Set(activeFields)
  orderedSortColumns.value = [
    ...activeFields.reverse().map((field) => columnsByField.get(field)),
    ...props.sort_columns.filter((column) => !activeFieldSet.has(column.field)),
  ]
}

const restoreControlsFromQuery = () => {
  const query = config.queries[path.value]
  const where = query?.where ?? {}

  for (const filter of props.filter_columns) {
    switch (filter.input_type) {
      case 'search':
        filters.value[filter.field] = getSearchValue(where, filter.field)
        break
      case 'dropdown':
        filters.value[filter.field] = getDropdownValue(where[filter.field])
        break
      case 'date':
        restoreDateControl(filter, where)
        break
    }
  }

  restoreSortControls(query?.sort ?? {})
}

restoreControlsFromQuery()
</script>

<style lang="scss">
.site-filter-toolbar {
  border-color: #e6ece9;
}

.site-filter-search-input.p-inputtext {
  border-color: var(--sats-control-border);
}

.site-filter-toggle.p-button:focus-visible {
  outline: 0.18rem solid var(--sats-focus-outline);
  outline-offset: 0.12rem;
  box-shadow: none;
}

.site-filter-dialog.p-dialog {
  max-height: calc(100dvh - 2rem);
  color: var(--sats-ink);
  border-color: var(--sats-border);
  box-shadow: 0 1.25rem 3.5rem rgba(17, 48, 38, 0.22);
}

.site-filter-dialog.p-dialog::before {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  left: 0;
  height: 0.25rem;
  content: '';
  background: linear-gradient(
    90deg,
    var(--sats-green) 0 70%,
    var(--sats-gold) 70% 85%,
    var(--sats-purple) 85% 100%
  );
}

.site-filter-dialog-header.p-dialog-header {
  color: var(--sats-ink);
  background: linear-gradient(
    135deg,
    rgba(var(--sats-green-rgb), 0.095),
    rgba(255, 255, 255, 0.98)
  );
}

.site-filter-dialog-title.p-dialog-title {
  color: var(--sats-ink);
  font-size: 1.35rem;
  letter-spacing: -0.015em;
}

.site-filter-close-button.p-dialog-header-icon {
  color: var(--sats-green);
}

.site-filter-close-button.p-dialog-header-icon:hover {
  color: #ffffff;
  background: var(--sats-green);
}

.site-filter-close-button.p-dialog-header-icon:focus-visible {
  outline: 0.18rem solid var(--sats-focus-outline);
  outline-offset: 0.12rem;
  box-shadow: none;
}

.site-filter-dialog-content.p-dialog-content {
  color: var(--sats-ink);
  background: var(--sats-surface-soft);
}

.site-filter-dialog-intro,
.site-filter-section-copy {
  color: var(--sats-muted);
  line-height: 1.45;
}

.site-filter-dialog-intro {
  font-size: 0.9rem;
}

.site-filter-section {
  border-color: var(--sats-border);
  border-radius: 0.85rem;
}

.site-filter-section--controls .site-filter-section-header {
  border-radius: 0.8rem 0.8rem 0 0;
}

.site-filter-section-header {
  background: linear-gradient(
    135deg,
    rgba(var(--sats-green-rgb), 0.075),
    rgba(255, 255, 255, 0.96)
  );
  border-bottom-color: var(--sats-border);
}

.site-filter-section-icon {
  width: 2.25rem;
  height: 2.25rem;
  color: var(--sats-green);
  background: var(--sats-green-soft);
}

.site-filter-section-heading {
  color: var(--sats-ink);
}

.site-filter-section-copy {
  font-size: 0.78rem;
}

.site-filter-label {
  color: var(--sats-ink);
  font-size: 0.78rem;
}

.site-filter-date-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 0.75rem;
}

.site-filter-control.p-dropdown,
.site-filter-control.p-calendar .p-inputtext {
  min-height: 3rem;
  color: var(--sats-ink);
  background: #ffffff;
  border-color: var(--sats-control-border);
  border-radius: 0.7rem;
  box-shadow: none;
}

.site-filter-control.p-dropdown:not(.p-disabled):hover,
.site-filter-control.p-calendar:not(.p-calendar-disabled) .p-inputtext:hover {
  border-color: rgba(var(--sats-green-rgb), 0.55);
}

.site-filter-control.p-dropdown:not(.p-disabled).p-focus,
.site-filter-control.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext {
  border-color: var(--sats-green);
  box-shadow: 0 0 0 0.2rem rgba(var(--sats-green-rgb), 0.13);
}

.site-filter-sort-table .p-tristatecheckbox .p-checkbox-box {
  background: var(--sats-control-surface) !important;
  border-color: var(--sats-green) !important;
}

.site-filter-sort-table .p-tristatecheckbox .p-checkbox-icon {
  color: var(--sats-green) !important;
}

.site-filter-sort-table .p-datatable-thead > tr > th {
  padding: 0.75rem;
  color: var(--sats-green-dark);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(var(--sats-green-rgb), 0.065);
  border-color: var(--sats-border);
}

.site-filter-sort-table .p-datatable-tbody > tr > td {
  padding: 0.8rem 0.75rem;
  color: var(--sats-ink);
  border-color: var(--sats-border);
}

.site-filter-sort-table .p-datatable-reorderablerow-handle {
  color: var(--sats-green);
}

.site-filter-sort-direction {
  min-width: 7.75rem;
}

.site-filter-sort-label {
  color: var(--sats-muted);
  font-size: 0.76rem;
}

.site-filter-sort-field {
  color: var(--sats-ink);
}

@media (max-width: 32rem) {
  .site-filter-date-grid {
    grid-template-columns: 1fr;
  }

  .site-filter-dialog-body {
    padding: 0.75rem !important;
  }

  .site-filter-sort-table .p-datatable-thead > tr > th,
  .site-filter-sort-table .p-datatable-tbody > tr > td {
    padding-right: 0.5rem;
    padding-left: 0.5rem;
  }

  .site-filter-sort-direction {
    min-width: 0;
  }

  .site-filter-sort-label {
    display: none;
  }
}
</style>
